import { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { tierTitleKey } from "src/business/subscription/tierLabel";
import { formatPrice } from "src/Utils/number";
import { CustomButton, CustomLoadingButton } from "src/design";
import { PaymentType } from "src/business/payment/payment";
import {
  BillingPeriod,
  monthsIn,
  priceFor,
} from "src/business/payment/billingPeriod";
import {
  PaymentError,
  resolvePaymentErrorKey,
} from "src/business/payment/paymentErrors";
import { upgradeableTiers } from "src/business/payment/upgradeTarget";
import { MAX_PROOF_SIZE_MB } from "src/business/payment/proofFile";
import { useDeclarePayment } from "src/data/payment/queries/payment";
import { SubscriptionTier } from "src/data/landing/queries/subscriptionTiers";
import {
  colorBorder,
  colorSurfaceHover,
  colorTextError,
  colorTextMuted,
  colorTextPrimary,
  radiusCard,
} from "src/design/tokens";
import { BankTransferCard } from "./BankTransferCard";
import { BillingPeriodSelector } from "./BillingPeriodSelector";
import { ProofFileInput } from "./ProofFileInput";

interface DeclarePaymentDialogProps {
  open: boolean;
  onClose: () => void;
  restaurantId: string;
  /** Used to derive the transfer reference shown with the bank details. */
  restaurantName: string;
  /** The restaurant's current tier, or `null` when it has no subscription. */
  currentTier: string | null;
  catalog: SubscriptionTier[] | undefined;
  /** Which flow to open on — the entry point decides. */
  initialType: PaymentType;
  /** Target tier carried in from a locked feature card, if any. */
  presetTier: string | null;
  /** Raised on a 409 so the page can reload the payment that blocked this one. */
  onConflict: () => void;
}

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <Grid item xs={12} md={6}>
    <Typography variant="caption" sx={{ color: colorTextMuted }}>
      {label}
    </Typography>
    <Typography sx={{ fontWeight: 600, color: colorTextPrimary }}>
      {value}
    </Typography>
  </Grid>
);

/**
 * Declaring a transfer. The client sends the proof, the type, the billing
 * period and — for an upgrade — the target tier. It never sends money: the
 * amount below is the catalog price for the chosen period, shown so the
 * restaurateur knows what to transfer, and the authoritative figure is whatever
 * the created payment reports back.
 */
export const DeclarePaymentDialog = ({
  open,
  onClose,
  restaurantId,
  restaurantName,
  currentTier,
  catalog,
  initialType,
  presetTier,
  onConflict,
}: DeclarePaymentDialogProps) => {
  const { t, i18n } = useTranslation();
  const [type, setType] = useState<PaymentType>(initialType);
  const [tier, setTier] = useState("");
  // The backend defaults an omitted period to MONTHLY; the form opens on the
  // same choice so what is shown and what is derived never diverge.
  const [period, setPeriod] = useState<BillingPeriod>("MONTHLY");
  const [proof, setProof] = useState<File | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const declarePayment = useDeclarePayment(restaurantId);
  const upgrades = upgradeableTiers(catalog, currentTier);

  // A renewal renews what the restaurant already has; only a restaurant with no
  // subscription record has to pick, because the backend derives nothing.
  const needsRenewalPicker = type === "RENEWAL" && !currentTier;
  const showPicker = type === "UPGRADE" || needsRenewalPicker;
  const pickerOptions = type === "UPGRADE" ? upgrades : (catalog ?? []);

  useEffect(() => {
    if (!open) return;

    setType(initialType);
    setProof(null);
    setErrorKey(null);
    setPeriod("MONTHLY");

    if (initialType === "RENEWAL") {
      setTier(currentTier ?? "");
      return;
    }
    // A preset tier the restaurant has since reached is not an upgrade any
    // more: drop it and let the picker open unfiltered.
    const preset = upgrades.find((option) => option.name === presetTier);
    setTier(preset?.name ?? "");
    // `upgrades` is derived from props and would re-run this on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialType, presetTier, currentTier]);

  // Only a renewal falls back to the current tier — that is what it renews. An
  // upgrade with nothing picked has no target: falling back here showed the
  // current plan's price as the expected amount and let the form submit a tier
  // the backend refuses with 422 for not being an upgrade.
  const selectedTier = type === "UPGRADE" ? tier : tier || currentTier || "";
  const plan = catalog?.find((option) => option.name === selectedTier);
  const canSubmit = !!proof && !!selectedTier && !declarePayment.isPending;

  const handleTypeChange = (next: PaymentType | null) => {
    if (!next) return;
    setType(next);
    setErrorKey(null);
    setTier(next === "RENEWAL" ? (currentTier ?? "") : "");
  };

  const handleSubmit = () => {
    if (!proof || !selectedTier) return;

    setErrorKey(null);
    declarePayment.mutate(
      { proof, type, targetTier: selectedTier, period },
      {
        onSuccess: onClose,
        onError: (error) => {
          setErrorKey(resolvePaymentErrorKey(error));
          // The conflict is another payment already awaiting review — the page
          // should show it rather than leaving an empty form behind.
          if (error instanceof PaymentError && error.status === 409) {
            onConflict();
          }
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={() => !declarePayment.isPending && onClose()}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        {t("payments.declare.title")}
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" sx={{ color: colorTextMuted, mb: 2 }}>
          {t("payments.declare.subtitle")}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="caption" sx={{ color: colorTextMuted }}>
              {t("payments.declare.typeLabel")}
            </Typography>
            <Box>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={type}
                onChange={(_event, next: PaymentType | null) =>
                  handleTypeChange(next)
                }
                sx={{ mt: 0.5 }}
              >
                <ToggleButton value="RENEWAL">
                  {t("payments.type.RENEWAL")}
                </ToggleButton>
                <ToggleButton value="UPGRADE" disabled={upgrades.length === 0}>
                  {t("payments.type.UPGRADE")}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {type === "UPGRADE" && upgrades.length === 0 && (
              <Typography variant="caption" sx={{ color: colorTextMuted }}>
                {t("payments.declare.highestTier")}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            {showPicker ? (
              <FormControl fullWidth size="small">
                <InputLabel>{t("payments.declare.tierLabel")}</InputLabel>
                <Select
                  label={t("payments.declare.tierLabel")}
                  value={tier}
                  onChange={(event) => setTier(event.target.value)}
                >
                  {pickerOptions.map((option) => (
                    <MenuItem key={option.name} value={option.name}>
                      {t(tierTitleKey(option.name), {
                        defaultValue: option.name,
                      })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <>
                <Typography variant="caption" sx={{ color: colorTextMuted }}>
                  {t("payments.declare.tierLabel")}
                </Typography>
                <Typography sx={{ fontWeight: 600, color: colorTextPrimary }}>
                  {t(tierTitleKey(selectedTier), {
                    defaultValue: selectedTier,
                  })}
                </Typography>
              </>
            )}
          </Grid>

          <SummaryRow
            label={t("payments.declare.durationLabel")}
            value={t("payments.months", { count: monthsIn(period) })}
          />

          <Grid item xs={12}>
            <BillingPeriodSelector
              tier={plan}
              value={period}
              onChange={setPeriod}
              disabled={declarePayment.isPending}
            />
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                border: `1px solid ${colorBorder}`,
                borderRadius: radiusCard,
                backgroundColor: colorSurfaceHover,
              }}
            >
              <Typography variant="caption" sx={{ color: colorTextMuted }}>
                {t("payments.declare.amountLabel")}
              </Typography>
              <Typography sx={{ fontWeight: 700, color: colorTextPrimary }}>
                {plan
                  ? t("payments.amount", {
                      amount: formatPrice(
                        priceFor(plan, period),
                        i18n.language,
                      ),
                    })
                  : "—"}
              </Typography>
              <Typography variant="caption" sx={{ color: colorTextMuted }}>
                {t("payments.declare.amountHint")}
              </Typography>
            </Box>
          </Grid>

          {/* The instructions belong here, not only on the page behind the
              dialog: this is the moment the transfer is actually being made. */}
          <Grid item xs={12}>
            <BankTransferCard
              restaurantName={restaurantName}
              restaurantId={restaurantId}
            />
          </Grid>

          <Grid item xs={12}>
            <ProofFileInput
              value={proof}
              onChange={setProof}
              disabled={declarePayment.isPending}
            />
          </Grid>

          {errorKey && (
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: colorTextError }}>
                {t(errorKey, { size: MAX_PROOF_SIZE_MB })}
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <CustomButton
          color="inverted"
          onClick={onClose}
          disabled={declarePayment.isPending}
        >
          {t("payments.declare.cancel")}
        </CustomButton>
        <CustomLoadingButton
          loading={declarePayment.isPending}
          disabled={!canSubmit}
          onSubmit={handleSubmit}
          label={t("payments.declare.submit")}
          fullwidth={false}
        />
      </DialogActions>
    </Dialog>
  );
};
